import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Building2, BarChart3, Target, MapPin, LogOut, Eye, TrendingUp, Route, DollarSign, Globe, ShoppingCart, Smartphone, Bell, Plug, CheckCircle2, AlertCircle, Plus, X, FileText, Trash2, Users, Shield, Sparkles } from 'lucide-react';
import { MapContainer, Circle as LeafletCircle, Polyline, Polygon, Popup } from 'react-leaflet';
import { OpenStreetMapTiles } from '../components/OpenStreetMapTiles';
import { IdleBubbleCircle } from '../components/IdleBubbleCircle';
import { I270_POLYGON } from '../lib/i270';
import { isValidPolygon, safePolygonPositions } from '../lib/leaflet-helpers';
import { AreaChart, Area, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import 'leaflet/dist/leaflet.css';
import OnboardingChecklist from '../components/OnboardingChecklist';
import ReconnectBanner from '../components/ReconnectBanner';
import AdvectiPixelCard from '../components/AdvectiPixelCard';
import ChatWidget from '../components/ChatWidget';
import PremiumZonesPanel from '../components/PremiumZonesPanel';
import { SocialAttributionTab } from '../components/SocialAttributionTab';
import { refreshSession, clearSession, startSessionKeepalive, ensureFreshToken } from '../lib/session';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdvertiserDashboard = () => {
  const navigate = useNavigate();
  const initialTab = (() => {
    try {
      const t = new URLSearchParams(window.location.search).get('tab');
      const valid = ['overview', 'analytics', 'conversions', 'bloom', 'sessions', 'integrations', 'privacy'];
      return valid.includes(t) ? t : 'overview';
    } catch { return 'overview'; }
  })();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [dashboardData, setDashboardData] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [conversionsData, setConversionsData] = useState(null);
  const [sessionsData, setSessionsData] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [geofences, setGeofences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [acceptingTerms, setAcceptingTerms] = useState(false);
  const [onboarding, setOnboarding] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('advecti_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const handleAuthError = async () => {
    // Try a silent renew before giving up — the access token lasts 2 hours but the
    // refresh cookie lasts 30 days, so most 401s here are recoverable.
    const fresh = await refreshSession();
    if (fresh) {
      fetchDashboard();
      return;
    }
    await clearSession();
    navigate('/advertiser-login');
  };

  useEffect(() => {
    let stop = () => {};
    (async () => {
      // Renew first if the stored token is stale, so the four fetches below don't
      // each 401 and race each other into a blank dashboard.
      const token = await ensureFreshToken();
      if (!token) {
        // Genuinely dead session — don't fire four doomed fetches, each of which
        // would retry the refresh on its own.
        await clearSession();
        navigate('/advertiser-login');
        return;
      }
      fetchDashboard();
      fetchNotifications();
      fetchGeofences();
      fetchOnboarding();
      stop = startSessionKeepalive();
    })();
    return () => stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab === 'analytics' && !analyticsData) fetchAnalytics();
    if (activeTab === 'conversions' && !conversionsData) fetchConversions();
    if (activeTab === 'sessions' && !sessionsData) fetchSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchDashboard = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/dashboard`, {
        headers: getAuthHeaders(),
      });
      if (res.status === 401) { handleAuthError(); return; }
      if (!res.ok) throw new Error('Failed to load');
      const data = await res.json();
      setDashboardData(data);
      // First-login terms gate: show modal if advertiser hasn't accepted yet.
      if (data?.advertiser && !data.advertiser.terms_accepted_at) {
        setShowTermsModal(true);
      }
    } catch (err) {
      console.error('fetchDashboard failed:', err);
      setError('Failed to load dashboard. Please log in again.');
      setTimeout(() => navigate('/advertiser-login'), 2000);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/notifications`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (err) { console.error('fetchNotifications failed:', err); }
  };

  const markNotificationsRead = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/auth/notifications/read`, {
        method: 'POST', headers: getAuthHeaders(),
      });
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (err) { console.error('markNotificationsRead failed:', err); }
  };

  const fetchGeofences = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/geofences`);
      if (res.ok) {
        const data = await res.json();
        setGeofences(data.geofences || []);
      }
    } catch (err) { console.error('fetchGeofences failed:', err); }
  };

  const fetchOnboarding = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/onboarding-status`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) setOnboarding(await res.json());
    } catch (err) { console.error('fetchOnboarding failed:', err); }
  };

  const fetchAnalytics = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/analytics`, {
        headers: getAuthHeaders(),
      });
      if (res.status === 401) { handleAuthError(); return; }
      if (res.ok) setAnalyticsData(await res.json());
    } catch (err) { console.error('fetchAnalytics failed:', err); }
  };

  const fetchConversions = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/conversions`, {
        headers: getAuthHeaders(),
      });
      if (res.status === 401) { handleAuthError(); return; }
      if (res.ok) setConversionsData(await res.json());
    } catch (err) { console.error('fetchConversions failed:', err); }
  };

  const fetchSessions = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/sessions`, {
        headers: getAuthHeaders(),
      });
      if (res.status === 401) { handleAuthError(); return; }
      if (res.ok) setSessionsData(await res.json());
    } catch (err) { console.error('fetchSessions failed:', err); }
  };

  const updateSalesTracking = async (enabled) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/preferences/sales-tracking`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      });
      if (res.status === 401) { handleAuthError(); return; }
      if (!res.ok) return;
      setDashboardData(prev => prev
        ? { ...prev, advertiser: { ...prev.advertiser, sales_tracking_enabled: enabled } }
        : prev);
      if (enabled && !conversionsData) fetchConversions();
      if (!enabled && activeTab === 'conversions') setActiveTab('overview');
    } catch (err) { console.error('updateSalesTracking failed:', err); }
  };

  const handleLogout = async () => {
    await clearSession();
    navigate('/advertiser-login');
  };

  const acceptTerms = async () => {
    setAcceptingTerms(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/accept-terms`, {
        method: 'POST', headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setDashboardData(prev => prev ? {
          ...prev,
          advertiser: { ...prev.advertiser, terms_accepted_at: data.terms_accepted_at }
        } : prev);
        setShowTermsModal(false);
      }
    } catch (err) {
      console.error('acceptTerms failed:', err);
    } finally {
      setAcceptingTerms(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-cream-light min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-navy border-t-transparent rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-cream-light min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <Link to="/advertiser-login" className="text-navy underline">Back to Login</Link>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    // Session recovered but the fetch hasn't landed (or failed) — never render the
    // shell with no data, that shows as a blank white page.
    return (
      <div className="bg-cream-light min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-navy border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-navy/60 text-sm">Loading your campaign…</p>
        </div>
      </div>
    );
  }

  const { advertiser, campaigns } = dashboardData || {};

  const salesTracking = !!advertiser?.sales_tracking_enabled;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Target },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'bloom', label: 'Social Attribution', icon: Sparkles },
    ...(salesTracking ? [{ id: 'conversions', label: 'Conversions', icon: TrendingUp }] : []),
    { id: 'sessions', label: 'Sessions', icon: Route },
    { id: 'integrations', label: 'Integrations', icon: Plug },
    { id: 'privacy', label: 'Privacy & Data', icon: Shield },
  ];

  return (
    <div className="bg-cream-light min-h-screen" data-testid="advertiser-dashboard">
      {/* First-login Terms acceptance modal */}
      {showTermsModal && (
        <div className="fixed inset-0 z-[9999] bg-navy/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="terms-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full my-auto flex flex-col" style={{ maxHeight: 'calc(100vh - 2rem)' }}>
            <div className="px-7 py-4 border-b border-navy/10 flex items-center gap-3 flex-shrink-0">
              <Shield className="text-navy" size={22} />
              <h2 className="text-xl font-bold text-navy">Welcome to Advecti — One quick thing</h2>
            </div>
            <div className="px-7 py-4 overflow-y-auto text-navy/80 text-sm leading-relaxed space-y-3 flex-1 min-h-0">
              <p>
                Before you start using your dashboard, please review and accept our{' '}
                <a href="/terms" target="_blank" rel="noreferrer" className="underline font-semibold text-navy hover:text-navy-dark" data-testid="terms-modal-tos-link">Terms of Service</a>{' '}
                and{' '}
                <a href="/privacy" target="_blank" rel="noreferrer" className="underline font-semibold text-navy hover:text-navy-dark" data-testid="terms-modal-privacy-link">Privacy Policy</a>.
              </p>
              <p>Key points you're agreeing to:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>We track aggregated impressions for your campaign via geofenced GPS sessions from our driver app and (optionally) BLE/Wi-Fi device scans.</li>
                <li>You authorize us to store data you connect through integrations (Shopify, Square, Stripe, Advecti Pixel, manual entries) <strong>for lift attribution only</strong> — we never sell it.</li>
                <li>
                  <strong>Early-termination policy.</strong> Base monthly rate: Testimonial <strong>$681</strong>, Partial <strong>$850</strong>, Full <strong>$1,500</strong>. Campaigns longer than 3 months get a volume discount of <strong>$85/mo</strong> (Partial) or <strong>$136/mo</strong> (Full) per month beyond 3. If you cancel early:
                  <ul className="list-[circle] pl-5 mt-1 space-y-1">
                    <li>You pay the <strong>full base rate</strong> for any remaining months that fall on or before month 3.</li>
                    <li>You pay back the <strong>volume discount</strong> for the months you've already used.</li>
                    <li>Any months remaining <strong>after month 3 are forgiven</strong>.</li>
                  </ul>
                </li>
                <li>You can request deletion of all your data at any time from the Data &amp; Privacy tab.</li>
              </ul>
              <p className="text-navy/60 text-xs pt-1">By clicking "I Agree", you confirm you have authority to bind your business and you accept the linked Terms and Privacy Policy.</p>
            </div>
            <div className="px-7 py-3 border-t border-navy/10 bg-cream-light/50 flex items-center justify-end gap-3 flex-shrink-0">
              <button
                onClick={handleLogout}
                className="px-5 py-2.5 text-sm text-navy/70 hover:text-navy underline"
                data-testid="terms-modal-decline-btn"
              >
                Decline and log out
              </button>
              <button
                onClick={acceptTerms}
                disabled={acceptingTerms}
                className={`px-6 py-2.5 rounded-full text-sm font-bold ${
                  acceptingTerms ? 'bg-navy/50 text-cream/70 cursor-not-allowed' : 'bg-navy text-cream hover:bg-navy-dark'
                }`}
                data-testid="terms-modal-accept-btn"
              >
                {acceptingTerms ? 'Saving…' : 'I Agree'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <section className="bg-navy py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-cream/20 rounded-full flex items-center justify-center">
              <Building2 className="text-cream" size={24} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-cream" data-testid="dashboard-company-name">{advertiser?.company_name}</h1>
              <p className="text-cream text-sm break-all" data-testid="dashboard-advertiser-email">{advertiser?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* Notifications Bell */}
            <div className="relative">
              <button
                onClick={() => { setShowNotifications(!showNotifications); if (!showNotifications && unreadCount > 0) markNotificationsRead(); }}
                className="relative text-cream/70 hover:text-cream transition-colors"
                data-testid="notifications-btn"
              >
                <Bell size={20} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
              {showNotifications && (
                <div className="absolute right-0 top-10 w-80 bg-white rounded-xl shadow-xl border border-navy/10 z-50 max-h-96 overflow-y-auto" data-testid="notifications-panel">
                  <div className="p-3 border-b border-navy/10">
                    <h3 className="font-bold text-navy text-sm">Notifications</h3>
                  </div>
                  {notifications.length === 0 ? (
                    <div className="p-4 text-center text-navy/50 text-sm">No notifications yet</div>
                  ) : (
                    notifications.slice(0, 20).map((notif) => (
                      <div key={notif.id} className={`p-3 border-b border-navy/5 ${!notif.read ? 'bg-blue-50/50' : ''}`}>
                        <p className="text-navy text-sm font-medium">{notif.title}</p>
                        <p className="text-navy/60 text-xs mt-0.5">{notif.message}</p>
                        <p className="text-navy/40 text-xs mt-1">{new Date(notif.created_at).toLocaleString()}</p>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            <button onClick={handleLogout} className="flex items-center gap-2 text-cream/70 hover:text-cream transition-colors" data-testid="logout-btn">
              <LogOut size={18} />
              <span className="hidden sm:inline">Log Out</span>
            </button>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <div className="bg-white border-b border-navy/10 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-4 font-medium text-sm whitespace-nowrap border-b-2 transition-all ${
                  activeTab === tab.id
                    ? 'text-navy border-navy'
                    : 'text-navy/50 border-transparent hover:text-navy hover:border-navy/20'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon size={16} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Reconnect banner: shown whenever any integration is in needs_reauth */}
        <ReconnectBanner />
        {/* Last login security strip + Onboarding checklist (show on Overview only) */}
        {activeTab === 'overview' && (
          <>
            {advertiser?.last_login_at && (
              <div className="bg-navy/5 border border-navy/10 rounded-lg px-4 py-2.5 mb-4 text-xs sm:text-sm text-navy/80 flex items-center justify-between flex-wrap gap-2" data-testid="last-login-banner">
                <span>
                  <Shield className="inline mr-1 text-navy/60" size={14} />
                  Last login: <strong>{new Date(advertiser.last_login_at).toLocaleString()}</strong>
                  {advertiser.last_login_ip ? <> from <code className="text-navy/60">{advertiser.last_login_ip}</code></> : null}
                </span>
                <span className="text-navy/50">Not you? <a href="/forgot-password" className="underline hover:text-navy">Reset your password</a>.</span>
              </div>
            )}
            <OnboardingChecklist data={onboarding} onConnectInstagram={() => setActiveTab('bloom')} />
          </>
        )}

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            <OverviewTab campaigns={campaigns} />
            <div className="mt-6">
              <PremiumZonesPanel
                centerLat={campaigns[0]?.center_lat}
                centerLng={campaigns[0]?.center_lng}
                onZonesSaved={fetchDashboard}
              />
            </div>
          </>
        )}
        {/* Analytics Tab */}
        {activeTab === 'analytics' && <AnalyticsTab data={analyticsData} />}
        {/* Conversions Tab — only when the advertiser opted into sales tracking */}
        {activeTab === 'conversions' && (
          salesTracking
            ? <ConversionsTab data={conversionsData} campaigns={campaigns} />
            : <SalesTrackingOptIn onEnable={() => updateSalesTracking(true)} />
        )}
        {/* Bloom Attribution Tab */}
        {activeTab === 'bloom' && <SocialAttributionTab />}
        {/* Sessions Tab */}
        {activeTab === 'sessions' && <SessionsTab data={sessionsData} geofences={geofences} campaigns={campaigns} />}
        {/* Integrations Tab */}
        {activeTab === 'integrations' && (
          <IntegrationsTab salesTracking={salesTracking} onSalesTrackingChange={updateSalesTracking} />
        )}
        {activeTab === 'privacy' && <PrivacyTab onAccountDeleted={handleLogout} />}
      </div>

      {/* AI assistant — floats bottom-right on every tab */}
      <ChatWidget />
    </div>
  );
};

// ===================== OVERVIEW TAB =====================
const OverviewTab = ({ campaigns }) => {
  const [today, setToday] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [progress, setProgress] = useState(null); // { month, target_hours, drivers: [...] }
  useEffect(() => {
    const token = localStorage.getItem('advecti_token');
    if (!token) return;
    const headers = { 'Authorization': `Bearer ${token}` };
    fetch(`${BACKEND_URL}/api/auth/impressions/today`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setToday(d); })
      .catch(() => {});
    fetch(`${BACKEND_URL}/api/auth/drivers`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.drivers) setDrivers(d.drivers); })
      .catch(() => {});
    fetch(`${BACKEND_URL}/api/auth/drivers-monthly-progress`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setProgress(d); })
      .catch(() => {});
  }, []);

  if (!campaigns || campaigns.length === 0) {
    return (
      <div className="text-center py-16">
        <BarChart3 className="text-navy/30 mx-auto mb-4" size={48} />
        <h2 className="text-xl font-bold text-navy mb-2">No Campaigns Yet</h2>
        <p className="text-navy/60 mb-6">Submit a campaign through our Get Started page.</p>
        <Link to="/get-started" className="bg-navy text-cream px-6 py-3 rounded-full font-semibold hover:bg-navy-dark transition-all">Get Started</Link>
      </div>
    );
  }

  return campaigns.map((campaign, idx) => (
    <div key={campaign.id || idx} className="mb-10">
      <h2 className="text-2xl font-bold text-navy mb-6 flex items-center gap-2">
        <Target size={24} />
        Campaign Details
        {campaigns.length > 1 && <span className="text-navy/50 text-lg ml-2">#{idx + 1}</span>}
      </h2>

      {/* TODAY tile — live counter + 24-bucket sparkline */}
      {today && idx === 0 && (
        <div className="bg-navy rounded-2xl p-6 mb-6 text-cream" data-testid="today-impressions-tile">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <p className="text-xs uppercase tracking-wide text-cream/60 font-semibold">Today's impressions</p>
              <p className="text-5xl font-bold mt-1">{(today.today_impressions || 0).toLocaleString()}</p>
              <p className="text-xs text-cream/70 mt-1">
                {today.sessions_today} session{today.sessions_today === 1 ? '' : 's'} · vs {(today.yesterday_impressions || 0).toLocaleString()} yesterday
                {today.yesterday_impressions > 0 && (
                  <span className={`ml-2 font-semibold ${today.today_impressions >= today.yesterday_impressions ? 'text-green-300' : 'text-amber-300'}`}>
                    {today.today_impressions >= today.yesterday_impressions ? '+' : ''}{(((today.today_impressions - today.yesterday_impressions) / today.yesterday_impressions) * 100).toFixed(1)}%
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-end gap-0.5 h-16" aria-label="hourly sparkline">
              {(today.hourly || []).map((v, i) => {
            const max = Math.max(...(today.hourly || [1]), 1);
            const h = Math.max(2, (v / max) * 64);
            const isCurrentHr = i === new Date().getHours();
            return (
              <div
                key={`hr-${i}`}
                className={`w-1.5 rounded-t ${isCurrentHr ? 'bg-cream' : 'bg-cream/40'}`}
                style={{ height: `${h}px` }}
                title={`${i}:00 — ${v.toLocaleString()} impressions`}
              />
            );
          })}
            </div>
          </div>
          <p className="text-xs text-cream/40 mt-2">Hourly distribution (today, midnight → now)</p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6">
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <div className="space-y-3">
            <DetailRow label="Status">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                campaign.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
              }`}>
                {campaign.status === 'active' ? 'Active' : 'Under Review'}
              </span>
            </DetailRow>
            <DetailRow label="Impressions/Day">
              <span className="text-navy/50 text-xs">Calculated from sessions →</span>
            </DetailRow>
            <DetailRow label="Duration">{campaign.campaign_duration} months</DetailRow>
            <DetailRow label="Wrap Type">{campaign.wrap_type === 'full_wrap' ? 'Full Wrap' : campaign.wrap_type === 'partial_wrap' ? 'Partial Wrap' : campaign.wrap_type}</DetailRow>
            <DetailRow label="Drivers"><span data-testid="campaign-driver-count">{campaign.driver_count || 1} driver{(campaign.driver_count || 1) === 1 ? '' : 's'}</span></DetailRow>
            <DetailRow label="Mini Zones">{campaign.mini_zones?.length || 0} zone(s)</DetailRow>
          </div>
        </div>
      </div>

      {/* Drivers card (only on first campaign) */}
      {idx === 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-navy/5 p-6 mt-6" data-testid="overview-drivers-card">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h3 className="text-lg font-bold text-navy flex items-center gap-2">
              <Users size={20} /> Your Drivers
              <span className="text-sm font-normal text-navy/50">({drivers.length})</span>
            </h3>
            {progress && (
              <span className="text-xs text-navy/60 font-medium bg-cream-light px-3 py-1 rounded-full">
                Each driver on their own {progress.cycle_days || 30}-day cycle · {progress.target_hours || 100}h driving + {progress.idle_target_hours || 100}h idle
              </span>
            )}
          </div>
          {drivers.length === 0 ? (
            <p className="text-sm text-navy/60">
              No drivers have joined your campaign yet. Drivers will appear here as soon as they accept.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-wider text-navy/60">
                    <th className="text-left py-2 pr-3">Driver</th>
                    <th className="text-left py-2 pr-3">Vehicle</th>
                    <th className="text-left py-2 pr-3">This cycle</th>
                    <th className="text-left py-2 pr-3 min-w-[180px]">Progress ({progress?.target_hours || 100}h drive · {progress?.idle_target_hours || 100}h idle)</th>
                    <th className="text-left py-2 pr-3">Tier</th>
                    <th className="text-right py-2 pr-3">Est. spend</th>
                  </tr>
                </thead>
                <tbody>
                  {drivers.map(dr => {
                    const v = dr.vehicle || {};
                    const vehicleStr = [v.year, v.color, v.make, v.model].filter(Boolean).join(' ') || '—';
                    // Match progress row by email first, then name (legacy sessions may only have name).
                    const p = (progress?.drivers || []).find(x =>
                      (x.driver_email && x.driver_email === dr.driver_email)
                      || (x.driver_name && x.driver_name === dr.driver_name)
                    ) || { driving_hours: 0, idle_hours: 0, total_hours: 0, est_spend: 0, tier: 1, tier_label: 'Tier 1', progress_pct: 0, idle_progress_pct: 0, cycle_complete: false, days_remaining: null };

                    const tierColor = {
                      1: 'bg-amber-100 text-amber-700 border-amber-200',
                      2: 'bg-orange-100 text-orange-700 border-orange-200',
                      3: 'bg-green-100 text-green-700 border-green-200',
                      4: 'bg-yellow-100 text-yellow-800 border-yellow-400',
                    }[p.tier] || 'bg-navy/10 text-navy/70';

                    const barColor = {
                      1: 'bg-amber-400',
                      2: 'bg-orange-400',
                      3: 'bg-green-500',
                      4: 'bg-yellow-500',
                    }[p.tier] || 'bg-navy/40';

                    return (
                      <tr key={dr.driver_email || dr.driver_name} className="border-t border-navy/5" data-testid={`driver-row-${dr.driver_email || dr.driver_name}`}>
                        <td className="py-2 pr-3">
                          <div className="font-semibold text-navy">{dr.driver_name || '—'}</div>
                          <div className="text-xs text-navy/50">{dr.driver_email}</div>
                        </td>
                        <td className="py-2 pr-3 text-navy/80">{vehicleStr}</td>
                        <td className="py-2 pr-3 text-navy/80 whitespace-nowrap" data-testid={`driver-hours-${dr.driver_email || dr.driver_name}`}>
                          <div className="flex items-baseline gap-1">
                            <span className="font-semibold">{p.driving_hours}h</span>
                            {p.idle_hours > 0 && <span className="text-navy/50 text-xs">+ {p.idle_hours}h idle</span>}
                          </div>
                          {p.days_remaining !== null && p.days_remaining !== undefined && (
                            <div className="text-[11px] text-navy/50">
                              {p.days_remaining === 0
                                ? <span className="text-amber-700 font-semibold">Cycle ends today</span>
                                : <span>{p.days_remaining}d left in cycle</span>}
                            </div>
                          )}
                        </td>
                        <td className="py-2 pr-3">
                          <div className="space-y-1.5">
                            <div>
                              <div className="flex items-baseline justify-between text-[10px] text-navy/50 mb-0.5">
                                <span>Driving</span>
                                <span>{p.progress_pct}%</span>
                              </div>
                              <div className="h-2 w-full bg-navy/10 rounded-full overflow-hidden">
                                <div
                                  className={`h-2 ${barColor} transition-all`}
                                  style={{ width: `${p.progress_pct}%` }}
                                  data-testid={`driver-progress-${dr.driver_email || dr.driver_name}`}
                                />
                              </div>
                            </div>
                            <div>
                              <div className="flex items-baseline justify-between text-[10px] text-navy/50 mb-0.5">
                                <span>Idle</span>
                                <span>{p.idle_progress_pct || 0}%</span>
                              </div>
                              <div className="h-2 w-full bg-navy/10 rounded-full overflow-hidden">
                                <div
                                  className="h-2 bg-purple-400 transition-all"
                                  style={{ width: `${p.idle_progress_pct || 0}%` }}
                                  data-testid={`driver-idle-progress-${dr.driver_email || dr.driver_name}`}
                                />
                              </div>
                            </div>
                          </div>
                          {p.cycle_complete && (
                            <span
                              className="inline-block mt-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-800 font-semibold border border-green-300"
                              data-testid={`driver-cycle-complete-${dr.driver_email || dr.driver_name}`}
                            >
                              ✓ Cycle complete
                            </span>
                          )}
                        </td>
                        <td className="py-2 pr-3">
                          <span
                            className={`inline-block px-2 py-0.5 text-xs font-semibold rounded-full border ${tierColor}`}
                            data-testid={`driver-tier-${dr.driver_email || dr.driver_name}`}
                          >
                            {p.tier_label}
                          </span>
                        </td>
                        <td className="py-2 pr-3 text-right font-semibold text-navy" data-testid={`driver-spend-${dr.driver_email || dr.driver_name}`}>
                          ${p.est_spend.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="text-xs text-navy/50 mt-3 italic">
                Each cycle is 100 hours of driving + 100 hours of idle-bubble time per driver.
                Once both are filled, the driver&apos;s cycle is complete and they must wait for the next {progress?.cycle_days || 30}-day cycle to start earning again.
                {progress?.monthly_rate ? (
                  <> Your plan is <strong className="text-navy/70">${Number(progress.monthly_rate).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo</strong> per driver.</>
                ) : null}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  ));
};

// ===================== ANALYTICS TAB =====================
const AnalyticsTab = ({ data }) => {
  const impressions = data?.impressions || [];
  const totalAll = impressions.reduce((s, d) => s + (d.total || 0), 0);
  const totalTargeted = impressions.reduce((s, d) => s + (d.targeted || 0), 0);
  const totalNonTargeted = impressions.reduce((s, d) => s + (d.non_targeted || 0), 0);

  if (impressions.length === 0) {
    return (
      <EmptyState
        icon={Eye}
        title="No Impression Data Yet"
        description="Impressions are automatically calculated from driver sessions based on zone."
      />
    );
  }

  const recentData = impressions.slice(0, 30).reverse();
  const maxTotal = Math.max(...recentData.map(d => d.total || 0), 1);

  return (
    <div>
      <h2 className="text-2xl font-bold text-navy mb-6 flex items-center gap-2"><Eye size={24} /> Impressions Analytics</h2>

      {/* Three summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-2xl p-5 shadow-sm border-l-4 border-navy" data-testid="stat-total-impressions">
          <span className="text-navy/50 text-sm">Total Impressions</span>
          <p className="text-3xl font-bold text-navy mt-1">{totalAll.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-2xl p-5 shadow-sm border-l-4 border-green-500" data-testid="stat-targeted-impressions">
          <span className="text-navy/50 text-sm">Targeted (Mini Zones)</span>
          <p className="text-3xl font-bold text-green-600 mt-1">{totalTargeted.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-2xl p-5 shadow-sm border-l-4 border-orange-400" data-testid="stat-non-targeted-impressions">
          <span className="text-navy/50 text-sm">Non-Targeted</span>
          <p className="text-3xl font-bold text-orange-500 mt-1">{totalNonTargeted.toLocaleString()}</p>
        </div>
      </div>

      {/* Stacked Bar Chart */}
      <div className="bg-white rounded-2xl p-6 shadow-sm mb-6">
        <h3 className="text-lg font-bold text-navy mb-1">Daily Impressions — Bar Chart</h3>
        <div className="flex gap-4 mb-4 text-xs">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-500 inline-block" /> Targeted</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-orange-400 inline-block" /> Non-Targeted</span>
        </div>
        <div className="flex items-end gap-1 h-56">
          {recentData.map((day) => {
            const tPct = day.total > 0 ? (day.targeted / maxTotal) * 100 : 0;
            const ntPct = day.total > 0 ? (day.non_targeted / maxTotal) * 100 : 0;
            return (
              <div key={day.date} className="flex-1 flex flex-col items-center justify-end h-full group relative">
                <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-navy text-cream text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                  {day.date}<br/>{day.total?.toLocaleString()} total
                </div>
                <div className="w-full flex flex-col justify-end" style={{ height: `${tPct + ntPct}%`, minHeight: day.total > 0 ? '2px' : '0' }}>
                  <div className="w-full bg-green-500 rounded-t" style={{ height: tPct > 0 ? `${(tPct / (tPct + ntPct)) * 100}%` : '0', minHeight: day.targeted > 0 ? '2px' : '0' }} />
                  <div className="w-full bg-orange-400" style={{ height: ntPct > 0 ? `${(ntPct / (tPct + ntPct)) * 100}%` : '0', minHeight: day.non_targeted > 0 ? '2px' : '0' }} />
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex justify-between mt-2 text-xs text-navy/40">
          <span>{recentData[0]?.date}</span>
          <span>{recentData[recentData.length - 1]?.date}</span>
        </div>
      </div>

      {/* Line Chart */}
      <div className="bg-white rounded-2xl p-6 shadow-sm mb-6">
        <h3 className="text-lg font-bold text-navy mb-1">Daily Impressions — Trend Line</h3>
        <div className="flex gap-4 mb-4 text-xs">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-navy inline-block" /> Total</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-green-500 inline-block" /> Targeted</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-orange-400 inline-block" /> Non-Targeted</span>
        </div>
        <LineChart data={recentData} maxVal={maxTotal} />
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <h3 className="text-lg font-bold text-navy mb-4">Daily Breakdown</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-navy/10">
                <th className="text-left py-2 text-navy/60 font-medium">Date</th>
                <th className="text-right py-2 text-navy/60 font-medium">Total</th>
                <th className="text-right py-2 text-green-600 font-medium">Targeted</th>
                <th className="text-right py-2 text-orange-500 font-medium">Non-Targeted</th>
                <th className="text-right py-2 text-navy/60 font-medium">Sessions</th>
                <th className="text-right py-2 text-navy/60 font-medium">Hours</th>
              </tr>
            </thead>
            <tbody>
              {impressions.slice(0, 30).map((day) => (
                <tr key={day.date} className="border-b border-navy/5">
                  <td className="py-2 text-navy">{day.date}</td>
                  <td className="py-2 text-right text-navy font-bold">{day.total?.toLocaleString()}</td>
                  <td className="py-2 text-right text-green-600 font-medium">{day.targeted?.toLocaleString()}</td>
                  <td className="py-2 text-right text-orange-500 font-medium">{day.non_targeted?.toLocaleString()}</td>
                  <td className="py-2 text-right text-navy/70">{day.sessions_count || '—'}</td>
                  <td className="py-2 text-right text-navy/70">{day.total_hours?.toFixed(1) || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// SVG Line Chart Component
const LineChart = ({ data, maxVal }) => {
  if (!data || data.length < 2) return null;
  const W = 800;
  const H = 200;
  const pad = 4;

  const toPoints = (field) => data.map((d, i) => {
    const x = pad + (i / (data.length - 1)) * (W - pad * 2);
    const y = H - pad - ((d[field] || 0) / maxVal) * (H - pad * 2);
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-48" preserveAspectRatio="none">
      {/* Grid lines */}
      {[0.25, 0.5, 0.75].map(f => (
        <line key={f} x1={pad} x2={W - pad} y1={H - pad - f * (H - pad * 2)} y2={H - pad - f * (H - pad * 2)} stroke="#1e3a5f" strokeOpacity="0.08" />
      ))}
      {/* Non-targeted line */}
      <polyline fill="none" stroke="#fb923c" strokeWidth="2.5" strokeLinejoin="round" points={toPoints('non_targeted')} />
      {/* Targeted line */}
      <polyline fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinejoin="round" points={toPoints('targeted')} />
      {/* Total line */}
      <polyline fill="none" stroke="#1e3a5f" strokeWidth="3" strokeLinejoin="round" points={toPoints('total')} />
    </svg>
  );
};

// ===================== CONVERSIONS TAB =====================
const RevenueChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  return (
    <div className="bg-white border border-navy/10 rounded-lg px-3 py-2 shadow-md text-xs">
      <p className="font-semibold text-navy mb-1">{label}</p>
      <p className="text-navy">Revenue: <strong>${Number(row.revenue).toFixed(2)}</strong></p>
      <p className="mt-1 flex items-center gap-1.5">
        <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: row.phaseColor }} />
        <span className="text-navy/70">{row.phaseLabel}</span>
      </p>
    </div>
  );
};

const ConversionsTab = ({ data, campaigns }) => {
  const raw = data?.conversions || [];
  const campaign = (campaigns || [])[0] || {};
  // Attribution milestones (driver launches + channels) drive baseline/pivot.
  // Legacy fallback: use campaign_start_date if no milestones exist.
  const milestones = data?.milestones || [];
  const earliestMilestone = milestones.length > 0 ? milestones[0] : null;
  const campaignStart = earliestMilestone?.started_at
    || campaign.campaign_start_date
    || campaign.start_date
    || data?.campaign_start_date;
  const startDateMissing = !campaignStart;

  // Manual entry label (e.g. "Gym memberships") drives the Other lift card title.
  const [manualLabel, setManualLabel] = useState('Other');
  const [manualEnabled, setManualEnabled] = useState(false);
  useEffect(() => {
    const token = localStorage.getItem('advecti_token');
    if (!token) return;
    fetch(`${BACKEND_URL}/api/integrations/status`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d) return;
        setManualEnabled(!!d.configured?.manual);
        const cols = d.manual_entry_columns || {};
        if (d.manual_entry_label) setManualLabel(d.manual_entry_label);
        else if (cols.other) setManualLabel(cols.other);
      })
      .catch(() => {});
  }, []);

  // Aggregate by date — multiple sources (advecti_pixel, shopify, square, stripe, manual) can have
  // a row each per day. We sum them so the daily chart shows one bar per date.
  const byDate = new Map();
  for (const r of raw) {
    if (!r.date) continue;
    const acc = byDate.get(r.date) || { date: r.date };
    Object.keys(r).forEach(k => {
      if (k === 'date' || k === 'source' || k === 'advertiser_id' || k === 'unique_hashes') return;
      const v = r[k];
      if (typeof v === 'number') acc[k] = (acc[k] || 0) + v;
      else if (acc[k] === undefined) acc[k] = v;
    });
    byDate.set(r.date, acc);
  }
  // Sort ascending by date
  const days = Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));

  if (days.length === 0) {
    return (
      <EmptyState
        icon={TrendingUp}
        title="No Conversion Data Yet"
        description="Install the Advecti Pixel, or connect Shopify, Square, or Stripe in the Integrations tab to start measuring campaign lift."
      />
    );
  }

  // Per-day totals (handle missing fields gracefully).
  // Each day is assigned to its "phase" = the most-recently-started milestone
  // whose window contains the day (started_at <= day AND (ended_at is null OR day <= ended_at)).
  // Bars get tinted with that milestone's color so advertisers can see
  // attribution segments — including finite-window channels like a Facebook
  // Ads flight that ran June 15 → July 10.
  const enriched = days.map(d => {
    const revenue = (d.shopify_revenue || 0) + (d.square_revenue || 0) + (d.stripe_revenue || 0) + (d.manual_revenue || 0);
    const orders = (d.shopify_orders || 0) + (d.square_payments || 0) + (d.stripe_charges || 0) + (d.purchases || 0);
    // Find the latest active milestone on this day. Because milestones are
    // sorted by started_at asc, we walk from the end to find the latest that
    // still contains the day within its window.
    let phase = null;
    for (let i = milestones.length - 1; i >= 0; i--) {
      const m = milestones[i];
      if (m.started_at > d.date) continue; // hasn't started yet
      if (m.ended_at && m.ended_at < d.date) continue; // already ended before this day
      phase = m;
      break;
    }
    return {
      ...d,
      visits: d.website_visits || 0,
      newClients: (d.manual_other ?? d.manual_new_clients) || 0,
      orders,
      revenue: +revenue.toFixed(2),
      shopify: d.shopify_revenue || 0,
      square: d.square_revenue || 0,
      stripe: d.stripe_revenue || 0,
      manual: d.manual_revenue || 0,
      isCampaign: campaignStart ? d.date >= campaignStart : false,
      phaseId: phase?.id || '',
      phaseLabel: phase?.label || 'Baseline',
      phaseColor: phase?.color || '#94a3b8',
    };
  });

  // COMPARISON WINDOW:
  // Baseline = last 90 days before launch (matches the 90-day Shopify backfill).
  // Current  = ALL days from launch through today (uncapped).
  const baselineAll = enriched.filter(d => !d.isCampaign);
  const campaignAll = enriched.filter(d => d.isCampaign);
  const baseline = baselineAll.slice(-90);
  const duringCampaign = campaignAll;
  const equalizedDays = Math.min(baseline.length, duringCampaign.length);

  const avg = (arr, field) => arr.length ? arr.reduce((s, d) => s + (d[field] || 0), 0) / arr.length : 0;
  const computeLift = (field) => {
    const b = avg(baseline, field);
    const c = avg(duringCampaign, field);
    if (b === 0) return { baseline: 0, current: c, pct: c > 0 ? 100 : 0 };
    return { baseline: b, current: c, pct: ((c - b) / b) * 100 };
  };

  const headlineLift = computeLift('revenue');
  const newClientsLift = computeLift('newClients');
  const visitsLift = computeLift('visits');
  const ordersLift = computeLift('orders');
  const shopifyLift = computeLift('shopify');
  const squareLift = computeLift('square');
  const stripeLift = computeLift('stripe');

  const hasNewClientsData = enriched.some(d => d.newClients > 0);
  const hasVisitsData = enriched.some(d => d.visits > 0);
  const hasRevenueData = enriched.some(d => d.revenue > 0);

  // Admin override per advertiser. Two grouped toggles:
  //   revenue_lift     → controls Revenue + Website Visits cards together
  //   new_clients_lift → controls the Other (manual_other) card
  // "auto" = show if data exists, "on" = force-show, "off" = force-hide.
  const visibility = data?.metrics_visibility || { revenue_lift: 'auto', new_clients_lift: 'auto' };
  const showRevenue = visibility.revenue_lift === 'on'
    || (visibility.revenue_lift !== 'off' && hasRevenueData);
  const showVisits = visibility.revenue_lift === 'on'
    || (visibility.revenue_lift !== 'off' && hasVisitsData);
  const showNewClients = visibility.new_clients_lift === 'on'
    || (visibility.new_clients_lift !== 'off' && hasNewClientsData);

  // Per-source visibility — hide every mention of a platform that has no data.
  // (admin can also force a card off via metrics_visibility above.)
  const has = {
    visits:     enriched.some(d => d.visits > 0),
    shopify:    enriched.some(d => d.shopify > 0),
    square:     enriched.some(d => d.square > 0),
    stripe:     enriched.some(d => d.stripe > 0),
    manual:     enriched.some(d => d.manual > 0),
    newClients: showNewClients && visibility.new_clients_lift !== 'off',
  };
  // If admin force-disables New Clients, also wipe any new-client mentions.
  if (visibility.new_clients_lift === 'off') has.newClients = false;

  // Confidence label based on equalized window size on EACH side
  const confidenceLabel =
    equalizedDays >= 14 ? { label: 'Statistically meaningful', color: 'text-green-600' } :
    equalizedDays >= 7  ? { label: 'Early signal', color: 'text-amber-600' } :
    equalizedDays >= 3  ? { label: 'Preliminary', color: 'text-amber-600' } :
                          { label: 'Insufficient data', color: 'text-navy/50' };

  const totals = {
    revenue: enriched.reduce((s, d) => s + d.revenue, 0),
    visits: enriched.reduce((s, d) => s + d.visits, 0),
    orders: enriched.reduce((s, d) => s + d.orders, 0),
  };

  const fmtPct = (v) => (v > 0 ? '+' : '') + v.toFixed(1) + '%';
  const liftColor = (v) => v > 5 ? 'text-green-600' : v < -5 ? 'text-red-600' : 'text-navy/60';
  const liftBadge = (v) => v > 5 ? 'bg-green-50 text-green-700 border-green-200'
                          : v < -5 ? 'bg-red-50 text-red-700 border-red-200'
                          : 'bg-gray-50 text-gray-600 border-gray-200';

  return (
    <div data-testid="conversions-tab">
      <h2 className="text-2xl font-bold text-navy mb-6 flex items-center gap-2"><TrendingUp size={24} /> Conversion Tracking</h2>

      {/* Missing milestones prompt — replaces silent 50/50 split */}
      {startDateMissing && (
        <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-5 mb-6 flex items-start gap-3"
             data-testid="campaign-start-prompt">
          <AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={22} />
          <div className="flex-1">
            <p className="font-semibold text-navy">No attribution milestones set</p>
            <p className="text-sm text-navy/70 mt-1">
              We need to know when your first advertising signal went live (Driver 1&apos;s launch,
              your Facebook Ads start, etc.) to compute a fair Before vs During comparison. Without
              at least one milestone, your dashboard shows data totals but not lift %. Contact your
              Advecti rep to configure this on your account.
            </p>
          </div>
        </div>
      )}

      {/* Window info — visible when we have a real split */}
      {!startDateMissing && (
        <div className="bg-navy/5 rounded-xl px-4 py-2.5 mb-4 text-xs text-navy/70 flex flex-wrap items-center gap-x-4 gap-y-1"
             data-testid="comparison-window-info">
          <span><strong className="text-navy">Comparing:</strong></span>
          <span>last <strong>{baseline.length}</strong> day{baseline.length === 1 ? '' : 's'} before launch</span>
          <span className="text-navy/30">vs</span>
          <span><strong>{duringCampaign.length}</strong> day{duringCampaign.length === 1 ? '' : 's'} since launch</span>
          <span className="text-navy/30">·</span>
          <span>Launch date: <strong>{campaignStart}</strong></span>
        </div>
      )}

      {/* HEADLINE LIFT CARDS — up to 3 cards (Revenue / Other manual metric / Website visits) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {showRevenue && (
          <div className={`rounded-2xl p-6 shadow-sm bg-white border-2 ${headlineLift.pct > 5 ? 'border-green-200' : 'border-navy/10'}`} data-testid="lift-card-revenue">
            <p className="text-navy/60 text-sm uppercase tracking-wide font-semibold">Total Revenue Lift</p>
            <div className="flex items-baseline gap-3 mt-2">
              <h1 className={`text-5xl font-bold ${liftColor(headlineLift.pct)}`}>{fmtPct(headlineLift.pct)}</h1>
              <p className="text-navy/60">vs pre-campaign baseline</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-5">
              <div>
                <p className="text-xs text-navy/50">Before (avg/day)</p>
                <p className="text-xl font-bold text-navy">${headlineLift.baseline.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-xs text-navy/50">During (avg/day)</p>
                <p className="text-xl font-bold text-navy">${headlineLift.current.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-xs text-navy/50">Confidence</p>
                <p className={`text-xl font-bold ${confidenceLabel.color}`}>{confidenceLabel.label}</p>
                <p className="text-xs text-navy/40 mt-0.5">{baseline.length} before · {duringCampaign.length} since</p>
              </div>
            </div>
          </div>
        )}
        {showNewClients && (
          <div className={`rounded-2xl p-6 shadow-sm bg-white border-2 ${newClientsLift.pct > 5 ? 'border-purple-200' : 'border-navy/10'}`} data-testid="lift-card-other">
            <p className="text-navy/60 text-sm uppercase tracking-wide font-semibold">{manualLabel} Lift</p>
            <div className="flex items-baseline gap-3 mt-2">
              <h1 className={`text-5xl font-bold ${liftColor(newClientsLift.pct)}`}>{fmtPct(newClientsLift.pct)}</h1>
              <p className="text-navy/60">vs pre-campaign baseline</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-5">
              <div>
                <p className="text-xs text-navy/50">Before (avg/day)</p>
                <p className="text-xl font-bold text-navy">{newClientsLift.baseline.toFixed(1)}</p>
              </div>
              <div>
                <p className="text-xs text-navy/50">During (avg/day)</p>
                <p className="text-xl font-bold text-navy">{newClientsLift.current.toFixed(1)}</p>
              </div>
              <div>
                <p className="text-xs text-navy/50">Why this matters</p>
                <p className="text-sm text-navy/70 mt-1">{manualLabel} are the strongest signal of campaign-driven discovery.</p>
              </div>
            </div>
          </div>
        )}
        {showVisits && (
          <div className={`rounded-2xl p-6 shadow-sm bg-white border-2 ${visitsLift.pct > 5 ? 'border-blue-200' : 'border-navy/10'}`} data-testid="lift-card-visits">
            <p className="text-navy/60 text-sm uppercase tracking-wide font-semibold">Website Visits Lift</p>
            <div className="flex items-baseline gap-3 mt-2">
              <h1 className={`text-5xl font-bold ${liftColor(visitsLift.pct)}`}>{fmtPct(visitsLift.pct)}</h1>
              <p className="text-navy/60">vs pre-campaign baseline</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-5">
              <div>
                <p className="text-xs text-navy/50">Before (avg/day)</p>
                <p className="text-xl font-bold text-navy">{Math.round(visitsLift.baseline).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-navy/50">During (avg/day)</p>
                <p className="text-xl font-bold text-navy">{Math.round(visitsLift.current).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-navy/50">Why this matters</p>
                <p className="text-sm text-navy/70 mt-1">Site traffic captures awareness lift before purchases catch up.</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* UNIFIED REVENUE CHART — color-coded by attribution milestone */}
      <div className="bg-white rounded-2xl p-6 shadow-sm mb-6" data-testid="conversions-revenue-chart">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="text-lg font-bold text-navy">Daily Revenue</h3>
          <p className="text-xs text-navy/50">
            {milestones.length > 0
              ? `Bars tinted by attribution phase · dotted lines mark milestone launches`
              : 'Vertical dotted line = campaign launch'}
          </p>
        </div>
        <div style={{ width: '100%', height: 300 }}>
          <ResponsiveContainer>
            <BarChart data={enriched} margin={{ top: 24, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#64748b' }} />
              <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={(v) => `$${v}`} />
              <RechartsTooltip
                cursor={{ fill: 'rgba(30,58,95,0.04)' }}
                labelStyle={{ color: '#1e3a5f', fontWeight: 600 }}
                content={<RevenueChartTooltip />}
              />
              <Bar dataKey="revenue" radius={[2, 2, 0, 0]}>
                {enriched.map((d) => (
                  <Cell key={`cell-${d.date}`} fill={d.phaseColor} fillOpacity={d.phaseId ? 0.85 : 0.35} />
                ))}
              </Bar>
              {milestones.map((m) => (
                <ReferenceLine
                  key={`ml-${m.id}`}
                  x={m.started_at}
                  stroke={m.color}
                  strokeDasharray="6 4"
                  strokeWidth={2}
                  label={{
                    value: `↓ ${m.label}`,
                    position: 'top',
                    fill: m.color,
                    fontSize: 10,
                    fontWeight: 600,
                  }}
                />
              ))}
              {milestones.filter(m => m.ended_at).map((m) => (
                <ReferenceLine
                  key={`ml-end-${m.id}`}
                  x={m.ended_at}
                  stroke={m.color}
                  strokeDasharray="2 4"
                  strokeWidth={1.5}
                  strokeOpacity={0.7}
                  label={{
                    value: `${m.label} ends ↑`,
                    position: 'insideTopRight',
                    fill: m.color,
                    fontSize: 9,
                    fontWeight: 500,
                    fillOpacity: 0.75,
                  }}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
        {milestones.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-4 pt-3 border-t border-navy/5" data-testid="milestones-legend">
            <span className="text-xs uppercase tracking-wide text-navy/50 font-semibold">Attribution:</span>
            <span className="inline-flex items-center gap-1.5 text-xs text-navy/70">
              <span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: '#94a3b8', opacity: 0.4 }} />
              Baseline (pre-launch)
            </span>
            {milestones.map(m => (
              <span key={m.id} className="inline-flex items-center gap-1.5 text-xs text-navy/70" data-testid={`legend-${m.id}`}>
                <span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: m.color }} />
                {m.label}
                <span className="text-navy/40">
                  · {m.started_at}
                  {m.ended_at ? ` → ${m.ended_at}` : ' → present'}
                </span>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* PER-SOURCE TILES — only show platforms the advertiser actually uses */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {has.visits && (
          <SourceTile name="Website Visits" icon={Globe} color="text-orange-600" tone="bg-orange-500"
            current={visitsLift.current} liftPct={visitsLift.pct} liftBadge={liftBadge(visitsLift.pct)}
            format={(v) => Math.round(v).toLocaleString()} subtitle="Advecti Pixel" />
        )}
        {has.newClients && (
          <SourceTile name={manualLabel} icon={Smartphone} color="text-purple-700" tone="bg-purple-600"
            current={newClientsLift.current} liftPct={newClientsLift.pct} liftBadge={liftBadge(newClientsLift.pct)}
            format={(v) => v.toFixed(1)} subtitle={manualEnabled ? 'From your manual entries' : 'From your booking app'} />
        )}
        {has.shopify && (
          <SourceTile name="Shopify Revenue" icon={ShoppingCart} color="text-green-700" tone="bg-green-600"
            current={shopifyLift.current} liftPct={shopifyLift.pct} liftBadge={liftBadge(shopifyLift.pct)}
            format={(v) => '$' + v.toFixed(2)} subtitle="Daily avg" />
        )}
        {has.square && (
          <SourceTile name="Square Revenue" icon={ShoppingCart} color="text-gray-700" tone="bg-black"
            current={squareLift.current} liftPct={squareLift.pct} liftBadge={liftBadge(squareLift.pct)}
            format={(v) => '$' + v.toFixed(2)} subtitle="Daily avg" />
        )}
        {has.stripe && (
          <SourceTile name="Stripe Revenue" icon={DollarSign} color="text-indigo-700" tone="bg-indigo-600"
            current={stripeLift.current} liftPct={stripeLift.pct} liftBadge={liftBadge(stripeLift.pct)}
            format={(v) => '$' + v.toFixed(2)} subtitle="Daily avg" />
        )}
        {has.manual && (() => {
          const manualLift = computeLift('manual');
          return (
            <SourceTile name="Manual Revenue" icon={DollarSign} color="text-amber-700" tone="bg-amber-600"
              current={manualLift.current} liftPct={manualLift.pct} liftBadge={liftBadge(manualLift.pct)}
              format={(v) => '$' + v.toFixed(2)} subtitle="From your booking app" />
          );
        })()}
      </div>

      {/* DRILLDOWN TABLE — columns only appear for platforms the advertiser uses */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-navy">Daily Breakdown</h3>
          <p className="text-xs text-navy/50">
            {[
              has.visits ? `${totals.visits.toLocaleString()} visits` : null,
              `${totals.orders.toLocaleString()} orders`,
              hasRevenueData ? `$${totals.revenue.toFixed(2)}` : null,
            ].filter(Boolean).join(' · ')}
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="conversions-drilldown-table">
            <thead>
              <tr className="border-b border-navy/10">
                <th className="text-left py-2 text-navy/60 font-medium">Date</th>
                {has.visits     && <th className="text-right py-2 text-navy/60 font-medium">Visits</th>}
                {has.newClients && <th className="text-right py-2 text-navy/60 font-medium">{manualLabel}</th>}
                {has.shopify    && <th className="text-right py-2 text-navy/60 font-medium">Shopify</th>}
                {has.square     && <th className="text-right py-2 text-navy/60 font-medium">Square</th>}
                {has.stripe     && <th className="text-right py-2 text-navy/60 font-medium">Stripe</th>}
                {has.manual     && <th className="text-right py-2 text-navy/60 font-medium">Manual</th>}
                {hasRevenueData && <th className="text-right py-2 text-navy/60 font-medium">Revenue</th>}
                <th className="text-right py-2 text-navy/60 font-medium">Phase</th>
              </tr>
            </thead>
            <tbody>
              {[...enriched].reverse().slice(0, 60).map((day) => (
                <tr key={day.date} className="border-b border-navy/5">
                  <td className="py-2 text-navy">{day.date}</td>
                  {has.visits     && <td className="py-2 text-right text-navy">{day.visits?.toLocaleString() || '—'}</td>}
                  {has.newClients && <td className="py-2 text-right text-navy/80">{day.newClients || '—'}</td>}
                  {has.shopify    && <td className="py-2 text-right text-navy/80">{day.shopify ? '$' + day.shopify.toFixed(2) : '—'}</td>}
                  {has.square     && <td className="py-2 text-right text-navy/80">{day.square  ? '$' + day.square.toFixed(2)  : '—'}</td>}
                  {has.stripe     && <td className="py-2 text-right text-navy/80">{day.stripe  ? '$' + day.stripe.toFixed(2)  : '—'}</td>}
                  {has.manual     && <td className="py-2 text-right text-navy/80">{day.manual  ? '$' + day.manual.toFixed(2)  : '—'}</td>}
                  {hasRevenueData && <td className="py-2 text-right font-semibold text-navy">${day.revenue.toFixed(2)}</td>}
                  <td className="py-2 text-right">
                    <span
                      className="inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full border"
                      style={{
                        backgroundColor: `${day.phaseColor}22`,
                        color: day.phaseId ? day.phaseColor : '#64748b',
                        borderColor: `${day.phaseColor}55`,
                      }}
                      data-testid={`phase-chip-${day.date}`}
                    >
                      <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: day.phaseColor }} />
                      {day.phaseLabel}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const SourceTile = ({ name, icon: Icon, color, tone, current, liftPct, liftBadge, format, subtitle }) => (
  <div className="bg-white rounded-2xl p-5 shadow-sm border border-navy/5">
    <div className="flex items-center justify-between mb-2">
      <div className={`w-8 h-8 ${tone} rounded-lg flex items-center justify-center text-white`}>
        <Icon size={16} />
      </div>
      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${liftBadge}`}>
        {(liftPct > 0 ? '+' : '') + liftPct.toFixed(1)}%
      </span>
    </div>
    <p className="text-navy/60 text-xs">{name}</p>
    <p className={`text-2xl font-bold ${color}`}>{format(current)}</p>
    <p className="text-navy/40 text-xs mt-1">{subtitle}</p>
  </div>
);

// ===================== SESSIONS TAB =====================
const SessionsTab = ({ data, geofences = [], campaigns = [] }) => {
  const sessions = data?.sessions || [];
  const [selectedSession, setSelectedSession] = useState(null);

  if (sessions.length === 0) {
    return (
      <EmptyState
        icon={Route}
        title="No Driver Sessions Yet"
        description="Driver sessions will appear here as drivers complete routes. You'll see their route on a map, impressions earned, and cost breakdown."
      />
    );
  }

  const costPerImpression = (session) => {
    const c = session.cost || 0;
    const i = session.impressions || 0;
    return i > 0 ? (c / i).toFixed(4) : '—';
  };
  const costPerWebVisit = (session) => {
    // This would require linking to conversion data; show as N/A for now
    return 'See Conversions tab';
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-navy mb-6 flex items-center gap-2"><Route size={24} /> Driver Sessions</h2>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Session list */}
        <div className="lg:col-span-1 space-y-3 max-h-[600px] overflow-y-auto pr-2">
          {sessions.map((session, i) => (
            <button
              key={session.id || i}
              onClick={() => setSelectedSession(session)}
              className={`w-full text-left p-4 rounded-xl border transition-all ${
                selectedSession?.id === session.id
                  ? 'bg-navy text-cream border-navy'
                  : 'bg-white border-navy/10 hover:border-navy/30'
              }`}
              data-testid={`session-item-${i}`}
            >
              <div className="flex justify-between items-start mb-1">
                <span className={`font-semibold text-sm ${selectedSession?.id === session.id ? 'text-cream' : 'text-navy'}`}>{session.driver_name}</span>
                <span className={`text-xs ${selectedSession?.id === session.id ? 'text-cream/70' : 'text-navy/50'}`}>{session.date}</span>
              </div>
              {session.period_count > 1 && (
                <span
                  className={`inline-block text-[10px] font-semibold uppercase tracking-wide rounded-full px-2 py-0.5 mb-1 ${selectedSession?.id === session.id ? 'bg-cream/20 text-cream' : 'bg-navy/10 text-navy/70'}`}
                  data-testid={`session-period-badge-${i}`}
                >
                  Driving period {session.period_index} of {session.period_count}
                </span>
              )}
              <div className={`flex gap-4 text-xs ${selectedSession?.id === session.id ? 'text-cream/80' : 'text-navy/60'}`}>
                <span>{session.impressions?.toLocaleString()} impr.</span>
                <span>${(session.cost || 0).toFixed(2)}</span>
                <span>{(session.duration_hours || 0).toFixed(1)}h</span>
              </div>
            </button>
          ))}
        </div>

        {/* Session detail */}
        <div className="lg:col-span-2">
          {selectedSession ? (
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              {/* Route Map (interactive) - preferred when GPS points exist; falls back to static image */}
              {selectedSession.route && selectedSession.route.length > 0 ? (
                <div style={{ height: '400px' }}>
                  <MapContainer
                    key={selectedSession.id}
                    bounds={[[39.83, -83.23], [40.17, -82.76]]}
                    boundsOptions={{ padding: [20, 20] }}
                    minZoom={7}
                    maxZoom={18}
                    style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom={true}
                    zoomControl={true}
                  >
                    <OpenStreetMapTiles mapId="session-route-map" />
                    {/* I-270 boundary always visible for context */}
                    <Polygon
                      positions={I270_POLYGON}
                      pathOptions={{ color: '#f97316', fillColor: '#f97316', fillOpacity: 0.04, weight: 2, dashArray: '6 4' }}
                    >
                      <Popup><strong>I-270 Zone</strong><br />Outer beltway + 0.5mi buffer</Popup>
                    </Polygon>
                    {/* Admin-drawn geofences (targeted zones) */}
                    {geofences.filter(isValidPolygon).map((gf) => (
                      <Polygon key={gf.id} positions={safePolygonPositions(gf.polygon)} pathOptions={{ color: gf.color || '#3388ff', fillColor: gf.color || '#3388ff', fillOpacity: 0.18, weight: 2 }}>
                        <Popup><strong>{gf.name}</strong><br />{gf.impressions_per_hour?.toLocaleString()} impr/hr</Popup>
                      </Polygon>
                    ))}
                    {/* Advertiser's own targeted mini-zones (premium bubbles from Get Started) */}
                    {(campaigns[0]?.mini_zones || []).map((zone, i) => (
                      <LeafletCircle
                        key={`mz-${i}`}
                        center={[zone.lat, zone.lng]}
                        radius={(zone.radius || 1) * 1609.34}
                        pathOptions={{ color: '#0ea5e9', fillColor: '#0ea5e9', fillOpacity: 0.22, weight: 2, dashArray: '4 3' }}
                      >
                        <Popup><strong>Your premium zone {i + 1}</strong><br />{(zone.radius || 1)} mi radius</Popup>
                      </LeafletCircle>
                    ))}
                    <Polyline
                      positions={selectedSession.route.map(p => [p.lat, p.lng])}
                      pathOptions={{ color: '#6b7280', weight: 5, opacity: 0.9 }}
                    />
                    {/* Geographic idle radius from the session; duration never changes its size. */}
                    {(selectedSession.idle_bubbles || []).map((b, i) => (
                      <IdleBubbleCircle
                        key={`idle-${i}`}
                        bubble={b}
                        testId={`idle-bubble-${selectedSession.id || 'session'}-${i}`}
                      />
                    ))}
                    <LeafletCircle center={[selectedSession.route[0].lat, selectedSession.route[0].lng]} radius={200} pathOptions={{ color: '#22c55e', fillColor: '#22c55e', fillOpacity: 0.8, weight: 2 }}>
                      <Popup>Start</Popup>
                    </LeafletCircle>
                    <LeafletCircle center={[selectedSession.route[selectedSession.route.length-1].lat, selectedSession.route[selectedSession.route.length-1].lng]} radius={200} pathOptions={{ color: '#ef4444', fillColor: '#ef4444', fillOpacity: 0.8, weight: 2 }}>
                      <Popup>End</Popup>
                    </LeafletCircle>
                  </MapContainer>
                </div>
              ) : selectedSession.route_image ? (
                <div style={{ height: '300px' }} className="bg-gray-100">
                  <img
                    src={selectedSession.route_image.startsWith('data:') ? selectedSession.route_image : `data:image/png;base64,${selectedSession.route_image}`}
                    alt="Driver route"
                    className="w-full h-full object-contain"
                    data-testid="session-route-image"
                  />
                </div>
              ) : null}

              {/* Map legend (only when we have a route) */}
              {selectedSession.route && selectedSession.route.length > 0 && (
                <div className="px-6 pt-3 flex items-center gap-4 text-xs text-navy/70 flex-wrap" data-testid="session-map-legend">
                  <span className="flex items-center gap-1.5"><span className="inline-block w-4 h-1 rounded bg-gray-500" /> Driving</span>
                  <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-full bg-purple-600 opacity-50" /> Idle bubble (parked)</span>
                  <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-full bg-sky-500 opacity-50 border-2 border-sky-500 border-dashed" /> Your premium zone</span>
                  <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-full bg-green-500" /> Start</span>
                  <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500" /> End</span>
                </div>
              )}

              {/* Session Stats */}
              <div className="p-6">
                <h3 className="text-lg font-bold text-navy mb-4">{selectedSession.driver_name} — {selectedSession.date}</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <span className="text-navy/50 text-xs block">Impressions</span>
                    <span className="text-navy font-bold text-lg">{selectedSession.impressions?.toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-navy/50 text-xs block">Est. Total Cost</span>
                    <span className="text-navy font-bold text-lg">${(selectedSession.cost || 0).toFixed(2)}</span>
                    {selectedSession.hourly_rate ? (
                      <span className="text-navy/40 text-xs block mt-0.5">
                        ${selectedSession.hourly_rate.toFixed(2)}/hr driving · ${(selectedSession.idle_rate || 0).toFixed(2)}/hr idle
                      </span>
                    ) : null}
                  </div>
                  <div>
                    <span className="text-navy/50 text-xs block">Duration</span>
                    <span className="text-navy font-bold text-lg">{(selectedSession.duration_hours || 0).toFixed(1)} hrs</span>
                    {(selectedSession.driving_hours !== undefined || selectedSession.idle_hours !== undefined) ? (
                      <span className="text-navy/40 text-xs block mt-0.5">
                        <span className="text-gray-700 font-semibold">{(selectedSession.driving_hours || 0).toFixed(1)}h driving</span>
                        {' · '}
                        <span className="text-purple-700 font-semibold">{(selectedSession.idle_hours || 0).toFixed(1)}h idle</span>
                      </span>
                    ) : null}
                  </div>
                  <div>
                    <span className="text-navy/50 text-xs block">Cost / Impression</span>
                    <span className="text-navy font-bold text-lg">${costPerImpression(selectedSession)}</span>
                  </div>
                  <div>
                    <span className="text-navy/50 text-xs block">Cost / Website Visit</span>
                    <span className="text-navy/60 text-sm">{costPerWebVisit(selectedSession)}</span>
                  </div>
                  <div>
                    <span className="text-navy/50 text-xs block">Time</span>
                    <span className="text-navy font-medium text-sm">
                      {selectedSession.start_time?.slice(11, 16)} — {selectedSession.end_time?.slice(11, 16)}
                    </span>
                  </div>
                </div>
                {selectedSession.excluded_hours > 0 && (
                  <p className="mt-4 text-xs text-navy/50" data-testid="session-excluded-note">
                    {selectedSession.excluded_hours.toFixed(1)}h parked outside a premium bubble was left out of this day — not shown, not billed.
                  </p>
                )}
                {/* Zone Breakdown */}
                {selectedSession.zone_breakdown && Object.keys(selectedSession.zone_breakdown).length > 0 && (
                  <div className="mt-4 pt-4 border-t border-navy/10">
                    <span className="text-navy/50 text-xs block mb-2">Impressions by Zone</span>
                    <div className="space-y-1">
                      {Object.entries(selectedSession.zone_breakdown).map(([zone, impr]) => (
                        <div key={zone} className="flex justify-between text-sm">
                          <span className="text-navy/70">{zone}</span>
                          <span className="text-navy font-medium">{impr.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl p-12 shadow-sm text-center">
              <Route className="text-navy/20 mx-auto mb-3" size={40} />
              <p className="text-navy/50">Select a session to view the route and details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ===================== SHARED COMPONENTS =====================
const EmptyState = ({ icon: Icon, title, description, howTo }) => (
  <div className="text-center py-12 max-w-xl mx-auto">
    <div className="w-20 h-20 bg-navy/5 rounded-full flex items-center justify-center mx-auto mb-4">
      <Icon className="text-navy/30" size={36} />
    </div>
    <h2 className="text-xl font-bold text-navy mb-2">{title}</h2>
    <p className="text-navy/60">{description}</p>
    {howTo}
  </div>
);

const DetailRow = ({ label, children }) => (
  <div className="flex items-center justify-between py-2 border-b border-navy/5 last:border-0">
    <span className="text-navy/60 text-sm">{label}</span>
    <span className="text-navy font-medium text-sm">{children}</span>
  </div>
);

// ===================== INTEGRATIONS TAB =====================
const PrivacyTab = ({ onAccountDeleted }) => {
  const [confirmEmail, setConfirmEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [penalty, setPenalty] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [reportBusy, setReportBusy] = useState(false);

  const downloadMonthlyReport = async () => {
    setReportBusy(true);
    try {
      const token = localStorage.getItem('advecti_token');
      const res = await fetch(`${BACKEND_URL}/api/auth/monthly-report`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('report fetch failed');
      const r = await res.json();
      const fmt = (n) => `$${(n || 0).toLocaleString(undefined, {minimumFractionDigits:2,maximumFractionDigits:2})}`;
      const html = `<!doctype html><html><head><meta charset="utf-8"><title>Advecti Monthly Report — ${r.company_name} — ${r.month}</title>
<style>body{font-family:system-ui,sans-serif;max-width:780px;margin:40px auto;padding:0 24px;color:#1e3a5f;line-height:1.5}
h1{font-size:28px;margin:0 0 4px}h2{font-size:18px;margin:32px 0 8px;border-top:1px solid #1e3a5f22;padding-top:16px}
.meta{color:#1e3a5f99;font-size:14px}.grid{display:grid;grid-template-columns:repeat(2,1fr);gap:16px}
.card{background:#f7f5ee;border-radius:10px;padding:16px 18px}.k{font-size:11px;text-transform:uppercase;letter-spacing:.08em;color:#1e3a5f80}
.v{font-size:24px;font-weight:700;margin-top:2px}.print{margin-top:24px}
button{background:#1e3a5f;color:#fff;border:0;padding:10px 18px;border-radius:999px;font-weight:700;cursor:pointer}
@media print{.print{display:none}}</style></head><body>
<h1>${r.company_name || 'Advecti Advertiser'}</h1>
<div class="meta">Monthly Report · ${r.month} · Period ${r.period.start} → ${r.period.end}</div>
<h2>Impressions</h2><div class="grid">
<div class="card"><div class="k">Total impressions</div><div class="v">${r.impressions.total.toLocaleString()}</div></div>
<div class="card"><div class="k">Targeted (in-zone)</div><div class="v">${r.impressions.targeted.toLocaleString()}</div></div>
<div class="card"><div class="k">Non-targeted</div><div class="v">${r.impressions.non_targeted.toLocaleString()}</div></div>
<div class="card"><div class="k">Days with data</div><div class="v">${r.impressions.days_with_data}</div></div>
</div>
<h2>Driver sessions</h2><div class="card"><div class="k">Sessions completed</div><div class="v">${r.sessions_count}</div></div>
<h2>Conversions</h2><div class="grid">
<div class="card"><div class="k">Website visits</div><div class="v">${r.conversions.visits.toLocaleString()}</div></div>
<div class="card"><div class="k">Orders</div><div class="v">${r.conversions.orders.toLocaleString()}</div></div>
<div class="card"><div class="k">Revenue (all sources)</div><div class="v">${fmt(r.conversions.revenue)}</div></div>
<div class="card"><div class="k">Days of conversion data</div><div class="v">${r.conversions.rows}</div></div>
</div>
<div class="print"><button onclick="window.print()">Print or Save as PDF</button></div>
</body></html>`;
      const w = window.open('', '_blank');
      if (w) { w.document.write(html); w.document.close(); }
    } catch (err) {
      console.error('monthly report failed:', err);
    } finally {
      setReportBusy(false);
    }
  };

  const myEmail = (() => {
    try {
      const u = JSON.parse(localStorage.getItem('advecti_advertiser') || '{}');
      return u.email || '';
    } catch { return ''; }
  })();

  useEffect(() => {
    const token = localStorage.getItem('advecti_token');
    if (!token) return;
    fetch(`${BACKEND_URL}/api/auth/me/cancellation-preview`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setPenalty(d); })
      .catch(() => {});
  }, []);

  const hasPenalty = penalty && penalty.penalty_amount > 0;

  const handleDelete = async () => {
    const warning = hasPenalty
      ? `Your campaign has ${penalty.months_remaining} months remaining. Cancelling now incurs a $${penalty.penalty_amount.toFixed(2)} early-termination penalty that will be invoiced. Continue?`
      : 'This permanently deletes your account, all campaign data, integrations, and uploaded files. This action CANNOT be undone. Continue?';
    if (!window.confirm(warning)) return;
    setBusy(true);
    setError('');
    try {
      const token = localStorage.getItem('advecti_token');
      const res = await fetch(`${BACKEND_URL}/api/auth/me`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          confirm_email: confirmEmail.trim(),
          acknowledge_penalty: acknowledged,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setSuccess(true);
        setTimeout(() => { onAccountDeleted && onAccountDeleted(); }, 2500);
      } else {
        setError(typeof data.detail === 'string' ? data.detail : (data.detail?.message || 'Failed to delete account.'));
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-2xl mx-auto bg-white rounded-2xl border border-green-200 p-8 text-center">
        <CheckCircle2 className="text-green-600 mx-auto mb-4" size={48} />
        <h2 className="text-2xl font-bold text-navy mb-2">Account deleted</h2>
        <p className="text-navy/70">
          All of your data has been permanently removed.
          {hasPenalty && (
            <span className="block mt-2 text-sm text-amber-700">
              An invoice for ${penalty.penalty_amount.toFixed(2)} will be sent to {myEmail}.
            </span>
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="privacy-tab">
      <div>
        <h2 className="text-2xl font-bold text-navy mb-2">Privacy & Data</h2>
        <p className="text-navy/70">
          You have full control over your account and data. Disconnect integrations any time on the
          Integrations tab. To permanently delete everything we have on file, use the section below.
        </p>
      </div>

      {/* What we collect */}
      <div className="bg-white border border-navy/10 rounded-2xl p-6">
        <h3 className="text-lg font-bold text-navy mb-3">What we store about you</h3>
        <ul className="text-sm text-navy/80 list-disc list-inside space-y-1.5">
          <li>Your account: company name, email, phone, billing details</li>
          <li>Your campaigns: wrap type, duration, premium zones, driver assignments</li>
          <li>Session data: driver routes, idle bubbles, impressions, calculated cost</li>
          <li>Connected integrations: OAuth tokens for Shopify, Square, Stripe; visitor counters from the Advecti Pixel</li>
          <li>Conversion data pulled from connected sources or entered manually</li>
        </ul>
        <p className="text-xs text-navy/50 mt-4">
          See the full <Link to="/privacy" className="underline">Privacy Policy</Link> for retention timelines and your CCPA/GDPR rights.
        </p>
      </div>

      {/* Monthly report download */}
      <div className="bg-white border border-navy/10 rounded-2xl p-6">
        <div className="flex items-start gap-4 flex-wrap">
          <FileText className="text-navy mt-1" size={26} />
          <div className="flex-1 min-w-[260px]">
            <h3 className="text-lg font-bold text-navy mb-1">Last month's performance report</h3>
            <p className="text-navy/70 text-sm">
              Opens a print-friendly summary of last month's impressions, sessions, and conversions.
              Use your browser's print dialog to save as PDF.
            </p>
          </div>
          <button
            onClick={downloadMonthlyReport}
            disabled={reportBusy}
            data-testid="download-monthly-report-btn"
            className={`px-5 py-2.5 rounded-full font-bold text-sm ${
              reportBusy ? 'bg-navy/40 text-cream/70 cursor-not-allowed' : 'bg-navy text-cream hover:bg-navy-dark'
            }`}
          >
            {reportBusy ? 'Loading…' : 'View report'}
          </button>
        </div>
      </div>

      {/* Disconnect integrations — no penalty */}
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
        <h3 className="text-lg font-bold text-navy mb-2 flex items-center gap-2">
          <Plug size={20} /> Disconnect integrations
        </h3>
        <p className="text-sm text-navy/80 mb-3">
          You can revoke any third-party connection (Shopify, Square, Stripe, or Manual Entry)
          — or remove the Advecti Pixel from your site — without deleting your whole account
          &mdash; <strong>no penalty applies</strong>. We immediately drop the OAuth token and
          stop syncing data from that source.
        </p>
        <button
          onClick={() => { window.location.hash = '#integrations'; }}
          className="text-sm font-semibold text-navy underline hover:text-gold transition-colors"
          data-testid="goto-integrations"
        >
          Open Integrations tab →
        </button>
      </div>

      {/* Penalty notice (only shown if campaign is active with months remaining) */}
      {hasPenalty && (
        <div className="bg-amber-100 border-2 border-amber-300 rounded-2xl p-6" data-testid="penalty-notice">
          <h3 className="text-lg font-bold text-amber-900 mb-2 flex items-center gap-2">
            <AlertCircle size={20} /> Early-termination penalty applies
          </h3>
          <p className="text-sm text-amber-900 mb-3">
            Your campaign has <strong>{penalty.months_remaining} months</strong> remaining of its
            {' '}{penalty.duration_months}-month commitment. Under our two-part early-termination policy,
            you only pay for unfinished months that fall within the first 3 months of the campaign,
            plus the volume discount you received on months already used. Months beyond month 3 are forgiven.
          </p>
          <div className="bg-white rounded-lg p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
            <div>
              <p className="text-xs uppercase tracking-wide text-navy/60">Months billed at full rate</p>
              <p className="text-lg font-bold text-navy">{penalty.months_charged_at_full_rate} × ${penalty.full_monthly_rate.toFixed(0)}</p>
              <p className="text-xs text-navy/60 mt-0.5">= ${penalty.full_rate_charges.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-navy/60">Discount clawback</p>
              <p className="text-lg font-bold text-navy">{penalty.elapsed_months} mo × ${penalty.discount_per_extra_month.toFixed(0)}</p>
              <p className="text-xs text-navy/60 mt-0.5">= ${penalty.discount_clawback.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-emerald-700">Months forgiven</p>
              <p className="text-lg font-bold text-emerald-700">{penalty.months_forgiven}</p>
              <p className="text-xs text-emerald-700/70 mt-0.5">no charge</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-amber-700">Total penalty</p>
              <p className="text-2xl font-bold text-amber-700">${penalty.penalty_amount.toFixed(2)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Danger zone */}
      <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6">
        <h3 className="text-lg font-bold text-red-700 mb-2 flex items-center gap-2">
          <Trash2 size={20} /> Delete my account & all data
        </h3>
        <p className="text-sm text-navy/80 mb-4">
          This permanently removes your advertiser record, all campaigns, sessions, integrations,
          manual entries, and uploaded files from our database. Active drivers will be notified
          and detached from your campaign. <strong>This cannot be undone.</strong>
        </p>
        <label className="block text-xs uppercase tracking-wide text-navy/60 font-semibold mb-1">
          To confirm, retype your account email:
        </label>
        <input
          type="email"
          value={confirmEmail}
          onChange={(e) => setConfirmEmail(e.target.value)}
          placeholder={myEmail || 'your account email'}
          className="w-full px-3 py-2 border border-red-300 rounded-lg text-sm mb-3 focus:outline-none focus:border-red-500"
          data-testid="confirm-email-input"
        />
        {hasPenalty && (
          <label className="flex items-start gap-2 text-sm text-navy/80 mb-3 cursor-pointer">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-red-600"
              data-testid="penalty-ack-checkbox"
            />
            <span>
              I understand that an invoice for <strong>${penalty.penalty_amount.toFixed(2)}</strong>
              {' '}will be issued for the remaining {penalty.months_remaining} months of my campaign.
            </span>
          </label>
        )}
        {error && (
          <p className="text-sm text-red-600 mb-3" data-testid="delete-account-error">{error}</p>
        )}
        <button
          onClick={handleDelete}
          disabled={
            busy
            || !confirmEmail
            || confirmEmail.toLowerCase() !== (myEmail || '').toLowerCase()
            || (hasPenalty && !acknowledged)
          }
          className="px-5 py-2.5 bg-red-600 text-white rounded-lg font-semibold text-sm hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          data-testid="delete-account-btn"
        >
          {busy ? 'Deleting…' : (hasPenalty ? `Pay $${penalty.penalty_amount.toFixed(2)} & delete account` : 'Permanently delete my account')}
        </button>
      </div>
    </div>
  );
};


const SalesTrackingOptIn = ({ onEnable }) => (
  <div className="bg-white rounded-2xl p-8 shadow-sm max-w-2xl" data-testid="sales-tracking-optin">
    <h2 className="text-2xl font-bold text-navy mb-2">Sales &amp; revenue lift is optional</h2>
    <p className="text-navy/70 mb-4">
      Your campaign is measured through Social Attribution — new followers and sweepstakes
      entries driven by your wrap. If you also want revenue and website lift measured against
      a pre-campaign baseline, turn on sales tracking and connect Shopify, Square, Stripe, or
      the Advecti Pixel.
    </p>
    <button
      onClick={onEnable}
      className="bg-navy text-cream px-6 py-3 rounded-full font-bold hover:bg-navy-dark transition-colors"
      data-testid="enable-sales-tracking-btn"
    >
      Turn on sales tracking
    </button>
  </div>
);

const SalesTrackingSwitch = ({ enabled, onChange }) => (
  <div className="bg-white rounded-2xl p-5 shadow-sm flex items-start justify-between gap-4 flex-wrap" data-testid="sales-tracking-switch-card">
    <div className="max-w-2xl">
      <h3 className="text-lg font-bold text-navy">Sales &amp; revenue lift tracking (optional)</h3>
      <p className="text-navy/60 text-sm mt-1">
        Off by default. Turn it on to unlock the Conversions tab and measure revenue + website
        visits against your pre-campaign baseline. Social Attribution keeps working either way.
      </p>
    </div>
    <button
      onClick={() => onChange(!enabled)}
      role="switch"
      aria-checked={enabled}
      className="relative w-14 h-8 rounded-full transition-colors flex-shrink-0 border border-navy/10"
      style={{ backgroundColor: enabled ? 'var(--navy)' : 'rgba(30, 58, 95, 0.18)' }}
      data-testid="sales-tracking-switch"
    >
      <span
        className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full shadow transition-transform ${enabled ? 'translate-x-6' : ''}`}
      />
    </button>
  </div>
);

const IntegrationsTab = ({ salesTracking, onSalesTrackingChange }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState(null);

  const [showManualForm, setShowManualForm] = useState(false);
  const [manualSourceLabel, setManualSourceLabel] = useState('My booking app');
  const [syncing, setSyncing] = useState(false);
  const [showShopifyToken, setShowShopifyToken] = useState(false);
  const [shopifyDomain, setShopifyDomain] = useState('');
  const [shopifyToken, setShopifyToken] = useState('');
  const newManualRow = () => ({
    _id: (crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`),
    date: new Date().toISOString().slice(0, 10),
    other: '', revenue: ''
  });
  const [manualRows, setManualRows] = useState([newManualRow()]);

  const headers = { 'Authorization': `Bearer ${localStorage.getItem('advecti_token')}` };

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/integrations/status`, { headers });
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
      }
    } catch (e) {
      setBanner({ kind: 'error', text: 'Failed to load integrations' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Handle OAuth callback redirect query params
    const params = new URLSearchParams(window.location.search);
    const integ = params.get('integration');
    if (integ) {
      const st = params.get('status');
      const labels = { shopify: 'Shopify', square: 'Square', stripe: 'Stripe' };
      const label = labels[integ] || integ;
      if (st === 'connected') {
        setBanner({ kind: 'success', text: `${label} connected.` });
      } else if (st === 'error') {
        setBanner({ kind: 'error', text: `${label} connection failed: ${params.get('reason') || 'unknown'}` });
      }
      window.history.replaceState({}, '', window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startConnect = async (provider) => {
    if (provider === 'manual') {
      setShowManualForm(true);
      // smooth-scroll to the form on next tick
      setTimeout(() => {
        document.querySelector('[data-testid="manual-entry-form"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
      return;
    }
    setBusy(true);
    try {
      let res;
      if (provider === 'shopify') {
        const shop = window.prompt(
          "Enter your Shopify store's admin domain.\n\n" +
          "Format: your-store.myshopify.com\n" +
          "(NOT your public website — log into Shopify Admin to find it: it's in the URL after /store/)"
        );
        if (!shop) { setBusy(false); return; }
        res = await fetch(`${BACKEND_URL}/api/integrations/shopify/start`, {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ shop }),
        });
      } else {
        res = await fetch(`${BACKEND_URL}/api/integrations/${provider}/start`, { headers });
      }
      if (res.ok) {
        const { auth_url } = await res.json();
        window.location.href = auth_url;
      } else {
        const err = await res.json().catch(() => ({}));
        setBanner({ kind: 'info', text: err.detail || `${provider} not configured yet.` });
      }
    } finally {
      setBusy(false);
    }
  };

  const openShopifyTokenForm = () => {
    setShowShopifyToken(true);
    setTimeout(() => {
      document.querySelector('[data-testid="shopify-token-form"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const submitShopifyToken = async () => {
    if (!shopifyDomain.trim() || !shopifyToken.trim()) {
      setBanner({ kind: 'error', text: 'Enter both your store domain and the access token.' });
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/integrations/shopify/connect-token`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ shop: shopifyDomain, access_token: shopifyToken }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setBanner({ kind: 'success', text: `Shopify connected — ${data.shop_name || data.shop}. Daily orders + revenue will sync automatically.` });
        setShowShopifyToken(false);
        setShopifyDomain('');
        setShopifyToken('');
        await load();
      } else {
        setBanner({ kind: 'error', text: data.detail || 'Shopify connection failed.' });
      }
    } catch {
      setBanner({ kind: 'error', text: 'Connection request failed. Check your internet and try again.' });
    } finally {
      setBusy(false);
    }
  };

  const updateManualRow = (i, field, value) => {
    const next = [...manualRows];
    next[i] = { ...next[i], [field]: value };
    setManualRows(next);
  };

  const saveManualRows = async () => {
    const cleanRows = manualRows
      .filter(r => r.date && /^\d{4}-\d{2}-\d{2}$/.test(r.date))
      .map(r => ({
        date: r.date,
        other: parseFloat(r.other || 0) || 0,
        revenue: parseFloat(r.revenue || 0) || 0,
      }));
    if (!cleanRows.length) {
      setBanner({ kind: 'error', text: 'No valid rows — make sure each row has a date' });
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/integrations/manual/import-self`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          advertiser_id: 'unused',  // backend overrides with the authenticated user's id
          source_label: manualSourceLabel,
          rows: cleanRows,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setBanner({ kind: 'success', text: `Saved ${data.upserted} days. View on the Conversions tab.` });
        setManualRows([newManualRow()]);
        setShowManualForm(false);
        await load();
      } else {
        const err = await res.json().catch(() => ({}));
        setBanner({ kind: 'error', text: err.detail || 'Save failed' });
      }
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async (provider) => {
    const label = provider === 'manual' ? 'Manual entry' : provider.toUpperCase();
    if (!window.confirm(
      `Disconnect ${label}?\n\nYour dashboard will stop reading new data from this source. Past data already imported will remain.`
    )) return;
    setBusy(true);
    try {
      await fetch(`${BACKEND_URL}/api/integrations/${provider}/disconnect`, { method: 'POST', headers });
      await load();
      setBanner({ kind: 'success', text: `${provider.toUpperCase()} disconnected.` });
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="text-center py-16 text-navy/50">Loading integrations…</div>;

  const manualLabel = status?.manual_entry_label || 'Manual entry';
  const manualEnabled = !!status?.configured?.manual;
  const cols = status?.manual_entry_columns || {};
  const colOther = cols.other || manualLabel;
  const colRevenue = cols.revenue || 'Revenue ($)';

  const providers = [
    { id: 'shopify', name: 'Shopify', desc: 'Sync daily orders and revenue from your Shopify store.', color: 'bg-green-600' },
    { id: 'square', name: 'Square', desc: 'Sync daily Square payments and revenue (restaurants, retail, salons).', color: 'bg-black' },
    { id: 'stripe', name: 'Stripe', desc: 'Pull daily charges and revenue from your Stripe account.', color: 'bg-indigo-600' },
    ...(manualEnabled ? [{
      id: 'manual',
      name: manualLabel,
      desc: 'Log signups, bookings, or revenue numbers manually — perfect for businesses without an API.',
      color: 'bg-amber-600',
    }] : []),
  ];

  const addManualRow = () => {
    setManualRows([...manualRows, newManualRow()]);
  };
  const removeManualRow = (i) => {
    if (manualRows.length === 1) return;
    setManualRows(manualRows.filter((_, idx) => idx !== i));
  };

  const runSyncNow = async () => {
    setSyncing(true);
    setBanner(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/integrations/sync-now`, {
        method: 'POST', headers,
      });
      const data = await res.json();
      const ran = (data.run || []).filter(r => r.ran);
      if (ran.length === 0) {
        setBanner({ kind: 'info', text: 'No integrations are currently connected — connect one above and try again.' });
      } else {
        const ok = ran.filter(r => r.status === 'connected').map(r => r.provider);
        const bad = ran.filter(r => r.status !== 'connected');
        const counts = data.conversion_rows_last_30d || {};
        const countText = Object.entries(counts).map(([k, v]) => `${k}: ${v}`).join(', ') || 'none yet';
        if (bad.length === 0) {
          setBanner({ kind: 'success',
            text: `Synced ${ok.join(', ')}. Conversion rows in the last 30 days — ${countText}.` });
        } else {
          setBanner({ kind: 'error',
            text: `Issues syncing: ${bad.map(b => `${b.provider} (${b.error_reason || b.status})`).join('; ')}. ` +
                  (ok.length ? `Succeeded: ${ok.join(', ')}.` : '') });
        }
      }
      load();
    } catch (err) {
      console.error('sync-now failed:', err);
      setBanner({ kind: 'error', text: 'Sync request failed. Check your connection and try again.' });
    } finally {
      setSyncing(false);
    }
  };

  const anyConnected = !!(status?.connections &&
    Object.values(status.connections).some(c => c?.status === 'connected'));

  return (
    <div className="space-y-6" data-testid="integrations-tab">
      <SalesTrackingSwitch enabled={salesTracking} onChange={onSalesTrackingChange} />
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-navy flex items-center gap-2"><Plug size={24} /> Connect Your Tools</h2>
          <p className="text-navy/60 mt-1">
            Link your existing analytics + sales tools so Advecti can measure the real lift from your campaign.
          </p>
        </div>
        {anyConnected && (
          <button
            onClick={runSyncNow}
            disabled={syncing}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold ${
              syncing ? 'bg-navy/40 text-cream/70 cursor-not-allowed' : 'bg-navy text-cream hover:bg-navy-dark'
            }`}
            data-testid="sync-now-btn"
          >
            {syncing ? 'Syncing…' : 'Sync now'}
          </button>
        )}
      </div>

      {banner && (
        <div className={`p-4 rounded-xl border flex items-start gap-3 ${
          banner.kind === 'error' ? 'bg-red-50 border-red-200 text-red-700' :
          banner.kind === 'success' ? 'bg-green-50 border-green-200 text-green-700' :
          'bg-amber-50 border-amber-200 text-amber-700'
        }`}>
          {banner.kind === 'error' ? <AlertCircle size={20} /> : <CheckCircle2 size={20} />}
          <p className="flex-1 text-sm font-medium">{banner.text}</p>
          <button onClick={() => setBanner(null)} className="text-xs underline">Dismiss</button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {providers.map((p) => {
          const conn = status?.connections?.[p.id] || { status: 'disconnected' };
          const configured = status?.configured?.[p.id];
          const isConnected = conn.status === 'connected';
          return (
            <div key={p.id} className="bg-white rounded-2xl shadow-sm border border-navy/5 p-6 flex flex-col"
                 data-testid={`integration-card-${p.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className={`w-10 h-10 ${p.color} rounded-lg flex items-center justify-center text-white font-bold`}>
                  {p.name[0]}
                </div>
                {isConnected ? (
                  <span className="text-xs font-semibold bg-green-100 text-green-700 px-2 py-1 rounded-full flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full" /> Connected
                  </span>
                ) : configured ? (
                  <span className="text-xs font-semibold bg-gray-100 text-gray-600 px-2 py-1 rounded-full">Not connected</span>
                ) : (
                  <span className="text-xs font-semibold bg-amber-100 text-amber-700 px-2 py-1 rounded-full">Coming soon</span>
                )}
              </div>
              <h3 className="text-lg font-bold text-navy mb-1">{p.name}</h3>
              <p className="text-sm text-navy/60 mb-4 flex-1">{p.desc}</p>
              {isConnected ? (
                <div className="space-y-2">
                  <div className="text-xs text-navy/70 bg-cream rounded-lg p-2 space-y-0.5">
                    <p><span className="text-navy/50">Account:</span> {conn.account_label || '—'}</p>
                    {conn.property_label && <p><span className="text-navy/50">Property:</span> {conn.property_label}</p>}
                    {conn.last_synced_at && <p><span className="text-navy/50">Last sync:</span> {new Date(conn.last_synced_at).toLocaleString()}</p>}
                  </div>
                  <button onClick={() => disconnect(p.id)} disabled={busy}
                          className="w-full py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 font-semibold"
                          data-testid={`disconnect-${p.id}`}>
                    Disconnect
                  </button>
                </div>
              ) : p.id === 'shopify' ? (
                <button onClick={openShopifyTokenForm} disabled={busy}
                        className="w-full py-2.5 rounded-lg font-semibold text-sm bg-navy text-cream hover:bg-navy/90 transition"
                        data-testid="connect-shopify-token">
                  Connect Shopify
                </button>
              ) : (
                <button onClick={() => startConnect(p.id)} disabled={busy || !configured}
                        className={`w-full py-2.5 rounded-lg font-semibold text-sm transition ${
                          configured ? 'bg-navy text-cream hover:bg-navy/90' : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        }`}
                        data-testid={`connect-${p.id}`}>
                  {configured ? `Connect ${p.name.split(' ')[0]}` : 'Awaiting credentials'}
                </button>
              )}
              {p.id === 'stripe' && !isConnected && (
                <p className="text-[11px] text-navy/50 mt-2 leading-snug" data-testid="stripe-signin-hint">
                  Already use Stripe?{' '}
                  <a href="https://dashboard.stripe.com/login" target="_blank" rel="noreferrer" className="underline font-semibold">
                    Sign in to Stripe first
                  </a>{' '}
                  — then connecting is one click. If Stripe shows a signup form, use the "Sign in" link at the top of its page instead.
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Advecti Pixel — primary site-visit tracking (replaces GA4 entirely) */}
      <AdvectiPixelCard />

      {/* Shopify custom-app token connect (no Partner app / OAuth required) */}
      {showShopifyToken && (
        <div className="bg-white rounded-2xl shadow-sm border border-green-200 p-6 space-y-4" data-testid="shopify-token-form">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-navy">Connect Shopify with an access token</h3>
            <button
              onClick={() => setShowShopifyToken(false)}
              className="text-xs text-navy/50 hover:text-navy underline"
              data-testid="shopify-token-close"
            >
              Close
            </button>
          </div>
          <p className="text-sm text-navy/60">
            Takes about 2 minutes inside your Shopify admin. No app install from the App Store, no approval wait.
          </p>
          <ol className="text-sm text-navy/80 space-y-2 list-decimal pl-5">
            <li>Log in to your <strong>Shopify admin</strong> → <strong>Settings</strong> (bottom left) → <strong>Apps and sales channels</strong>.</li>
            <li>Click <strong>Develop apps</strong> (top right). If prompted, click <strong>Allow custom app development</strong> (twice).</li>
            <li>Click <strong>Create an app</strong> → name it <strong>Advecti</strong> → <strong>Create app</strong>.</li>
            <li>On the <strong>Configuration</strong> tab, next to "Admin API integration" click <strong>Configure</strong> → check <strong>read_orders</strong> → <strong>Save</strong>.</li>
            <li>Open the <strong>API credentials</strong> tab → click <strong>Install app</strong> → confirm.</li>
            <li>Under <strong>Admin API access token</strong>, click <strong>Reveal token once</strong> and copy it (starts with <code className="bg-cream px-1 rounded">shpat_</code>). Paste it below.</li>
          </ol>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-navy/50 font-semibold uppercase tracking-wide mb-1 block">Store domain</label>
              <input
                type="text"
                value={shopifyDomain}
                onChange={(e) => setShopifyDomain(e.target.value)}
                placeholder="your-store.myshopify.com"
                className="w-full px-3 py-2 border border-navy/20 rounded-lg text-sm"
                data-testid="shopify-domain-input"
              />
            </div>
            <div>
              <label className="text-xs text-navy/50 font-semibold uppercase tracking-wide mb-1 block">Admin API access token</label>
              <input
                type="password"
                value={shopifyToken}
                onChange={(e) => setShopifyToken(e.target.value)}
                placeholder="shpat_…"
                className="w-full px-3 py-2 border border-navy/20 rounded-lg text-sm"
                data-testid="shopify-token-input"
              />
            </div>
          </div>
          <p className="text-xs text-navy/50">
            On connect we'll automatically import your <strong>last 90 days</strong> of orders so your baseline and lift
            charts show data right away. The token is stored securely on our servers and only used to read order totals.
          </p>
          <button
            onClick={submitShopifyToken}
            disabled={busy}
            className="px-6 py-2.5 bg-green-700 text-white rounded-lg font-semibold text-sm hover:bg-green-800 disabled:opacity-50 transition"
            data-testid="shopify-token-submit"
          >
            {busy ? 'Connecting…' : 'Connect Shopify'}
          </button>
        </div>
      )}

      {/* Manual Entry self-serve form */}
      {manualEnabled && showManualForm && (
        <div className="bg-white rounded-2xl shadow-sm border border-amber-200 p-6 space-y-4" data-testid="manual-entry-form">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-navy">Log {manualLabel}</h3>
            <button
              onClick={() => setShowManualForm(false)}
              className="text-xs text-navy/50 hover:text-navy underline"
              data-testid="manual-entry-close"
            >
              Close
            </button>
          </div>
          <p className="text-sm text-navy/60">
            Enter daily numbers below. Each row upserts that date — re-saving the same date overwrites it.
          </p>
          <div>
            <label className="text-xs text-navy/50 font-semibold uppercase tracking-wide mb-1 block">Source label</label>
            <input
              type="text"
              value={manualSourceLabel}
              onChange={(e) => setManualSourceLabel(e.target.value)}
              placeholder="e.g. Gym memberships, MindBody, paper log"
              className="w-full px-3 py-2 border border-navy/20 rounded-lg text-sm"
              data-testid="manual-source-label"
            />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm table-fixed">
              <thead className="text-xs text-navy/50 uppercase">
                <tr>
                  <th className="text-left py-2 pr-3 w-[30%]">Date</th>
                  <th className="text-left py-2 px-3 w-[26%]">{colOther} <span className="normal-case font-normal">(optional)</span></th>
                  <th className="text-left py-2 px-3 w-[26%]">{colRevenue}</th>
                  <th className="py-2 w-[18%]"></th>
                </tr>
              </thead>
              <tbody>
                {manualRows.map((row, i) => (
                  <tr key={row._id} className="border-t border-navy/5">
                    <td className="py-1.5 pr-3">
                      <input type="date" value={row.date}
                             onChange={(e) => updateManualRow(i, 'date', e.target.value)}
                             className="w-full px-2 py-1.5 border border-navy/20 rounded-md text-xs"
                             data-testid={`manual-date-${i}`} />
                    </td>
                    <td className="py-1.5 px-3">
                      <input type="number" min="0" value={row.other}
                             onChange={(e) => updateManualRow(i, 'other', e.target.value)}
                             className="w-full px-2 py-1.5 border border-navy/20 rounded-md text-xs"
                             data-testid={`manual-other-${i}`} />
                    </td>
                    <td className="py-1.5 px-3">
                      <input type="number" min="0" step="0.01" value={row.revenue}
                             onChange={(e) => updateManualRow(i, 'revenue', e.target.value)}
                             className="w-full px-2 py-1.5 border border-navy/20 rounded-md text-xs"
                             data-testid={`manual-revenue-${i}`} />
                    </td>
                    <td className="py-1.5 pl-2 text-right">
                      <button
                        type="button"
                        onClick={() => removeManualRow(i)}
                        disabled={manualRows.length === 1}
                        className="text-xs text-red-500 hover:text-red-700 disabled:opacity-30 underline"
                        data-testid={`manual-remove-${i}`}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={addManualRow}
              className="text-sm text-navy underline hover:text-navy-dark"
              data-testid="manual-add-row"
            >
              + Add row
            </button>
            <button
              type="button"
              onClick={saveManualRows}
              disabled={busy}
              className="px-5 py-2 bg-navy text-cream rounded-lg font-semibold text-sm hover:bg-navy/90 disabled:opacity-50"
              data-testid="manual-save"
            >
              {busy ? 'Saving…' : 'Save entries'}
            </button>
          </div>
        </div>
      )}

      <div className="bg-navy/5 rounded-xl p-4 text-sm text-navy/70">
        <p className="font-semibold text-navy mb-1">How attribution works</p>
        <p>
          Each connected source contributes a daily metric to your Conversions tab. We compare the 90 days before
          your campaign launched (baseline) against every day since launch to compute lift %.
        </p>
      </div>
    </div>
  );
};

export default AdvertiserDashboard;
